import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Check, ChevronDown } from 'lucide-react';

import { s, gray, ink } from '../theme';
import { useProgramForm } from '../../../../context/ProgramFormContext';

/**
 * Collapsible section shell — step badge, title, required-field counter, issue count.
 *
 * Ported from `Program Entry Updates/src/components/ui/SectionCard.tsx`. Progress and
 * issues are read from context rather than passed down, so the seven section components
 * stay free of plumbing.
 */
const SectionCard = ({ id, step, title, description, children }) => {
    const { progressBySection, issuesBySection, openSections, toggleSection } = useProgramForm();

    const progress = progressBySection[id];
    const open = openSections[id];
    const issues = issuesBySection[id] || [];
    const complete = progress.complete && progress.total > 0;

    // The collapse needs `overflow: hidden` to clip the body while its height animates, but
    // leaving it on afterwards cuts off anything that overflows the section on purpose —
    // the address autocomplete suggestions and the help tooltips. So it is only worn while
    // moving. Timing off `open` rather than framer's callbacks keeps the open-on-first-render
    // case honest: AnimatePresence has `initial={false}`, so those panels never animate and
    // would otherwise wait forever for a completion event.
    const [settled, setSettled] = useState(open);

    useEffect(() => {
        if (!open) {
            setSettled(false);
            return undefined;
        }
        const timer = setTimeout(() => setSettled(true), 260);
        return () => clearTimeout(timer);
    }, [open]);

    return (
        <section
            id={id}
            aria-labelledby={`${id}-heading`}
            style={{ ...s.card, scrollMarginTop: '16px' }}
        >
            <h2 id={`${id}-heading`} style={s.srOnly}>{title}</h2>

            <button
                type="button"
                onClick={() => toggleSection(id)}
                aria-expanded={open}
                aria-controls={`${id}-panel`}
                style={{
                    ...s.cardHeader,
                    cursor: 'pointer',
                    borderBottom: open ? `1px solid ${gray[200]}` : '1px solid transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = gray[50]; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
                <span style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                    <span
                        style={complete ? { ...s.stepBadge, ...s.stepBadgeComplete } : s.stepBadge}
                        aria-hidden="true"
                    >
                        {complete ? <Check size={14} strokeWidth={3} /> : step}
                    </span>
                    <span style={{ minWidth: 0 }}>
                        <span style={s.sectionTitle}>{title}</span>
                        <span style={s.sectionBlurb}>{description}</span>
                    </span>
                </span>

                <span style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {issues.length > 0 ? (
                        <span style={s.warningPill}>
                            <AlertTriangle size={12} aria-hidden="true" />
                            {issues.length} to review
                        </span>
                    ) : ''}

                    {progress.total > 0 ? (
                        <span style={s.countPill}>
                            {progress.filled}/{progress.total} required
                        </span>
                    ) : ''}

                    <motion.span
                        animate={{ rotate: open ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ display: 'flex' }}
                    >
                        <ChevronDown size={16} color={ink[500]} aria-hidden="true" />
                    </motion.span>
                    <span style={s.srOnly}>{open ? 'Collapse section' : 'Expand section'}</span>
                </span>
            </button>

            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        id={`${id}-panel`}
                        key="panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: settled ? 'visible' : 'hidden' }}
                    >
                        <div style={s.cardBody}>{children}</div>
                    </motion.div>
                ) : ''}
            </AnimatePresence>
        </section>
    );
};

/**
 * Titled panel used inside a section to bracket a related run of fields — the price
 * tiers, the address block, and so on.
 */
export const SubGroup = ({ title, description, action, children }) => (
    <div style={s.subGroup}>
        <div
            style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '12px',
            }}
        >
            <div>
                <h3 style={s.groupTitle}>{title}</h3>
                {description ? <p style={s.hint}>{description}</p> : ''}
            </div>
            {action}
        </div>
        {children}
    </div>
);

export default SectionCard;
